import { BehaviorSubject } from 'rxjs';
import type {
  IPrintJobsDetailsState,
  IProductionTabDataRes,
  TabDataRes,
} from './printJobsDetails.state';
import { C_Report } from '../../../../graphql/generatedModel';
import { ProductionListRes } from '../../services/printJobs.service';

/**
 * A tab's already-loaded payload may only be reused while it still belongs to
 * the currently selected date period. Anything else — no stamp at all, or a
 * stamp left over from an earlier visit or an earlier selected date — counts as
 * stale and has to be re-fetched.
 *
 * Without this check the only freshness signal was "is `dataTabs[tab]` set at
 * all", which let a payload fetched before an order was deleted survive into a
 * later visit and keep showing the deleted order (WA-3769).
 */
export const isTabDataFreshForDatePeriod = (
  tabData: TabDataRes,
  datePeriod: IPrintJobsDetailsState['datePeriod'] | undefined,
): boolean => {
  const loadedDatePeriod = tabData?.loadedDatePeriod;
  if (!loadedDatePeriod || !datePeriod) return false;
  return (
    loadedDatePeriod.fromDate === datePeriod.fromDate &&
    loadedDatePeriod.toDate === datePeriod.toDate
  );
};

export class StateHelpers {
  stream$: BehaviorSubject<IPrintJobsDetailsState>;
  constructor(stream$: BehaviorSubject<IPrintJobsDetailsState>) {
    this.stream$ = stream$;
  }

  isReportForPreview(reportId: C_Report | null | undefined): boolean {
    return (
      reportId !== C_Report.R12_LABEL && reportId !== C_Report.R49_BAKING_LIST_2_COLUMNS_SMART_SCALE
    );
  }

  isReportPreviewEnabled(report: ProductionListRes[number]): boolean {
    const { addDaysForPreProduction } = report;
    const daysToProduce = addDaysForPreProduction ?? 0; // IF addDaysForPreProduction will be null or underined

    const { dataTabs } = this.stream$.getValue();
    const productionData = dataTabs.production as IProductionTabDataRes | undefined;
    if (productionData === undefined) return false;
    const { countOrders, countOrderPreProduction } = productionData;

    if (daysToProduce > 0 && countOrderPreProduction > 0) {
      return true;
    } else if (daysToProduce === 0 && countOrders > 0) {
      return true;
    } else {
      return false;
    }
  }
}
